import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Row, Col } from 'reactstrap';
import axios from 'axios';
import { getSpecialty } from 'config/specialtyConfig';
import { getSpecialtyFields } from 'config/subscriptionPlans';

const BoardsCountdownBanner = (props) => {
    const [countdown, setCountdown] = useState(null);
    const [examDate, setExamDate] = useState(null);
    const fields = getSpecialtyFields(props.activeSpecialty);

    useEffect(() => {
        const userDetails = props.userDetails;
        if (userDetails[fields.year] !== 'PGY 4') return;

        setExamDate(null);
        axios.post('/getBoardsExamDate', { specialty: props.activeSpecialty }).then(res => {
            if (res.data.success && res.data.date) {
                setExamDate(res.data.date);
            }
        }).catch(() => {});
    }, [props.userDetails, props.activeSpecialty]);

    useEffect(() => {
        if (!examDate) return;

        const tick = () => {
            // Use US Eastern Time so countdown is consistent for all US students
            const nowET = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
            const exam = new Date(examDate + 'T00:00:00');
            const diff = exam.getTime() - nowET.getTime();
            if (diff < 0) {
                setCountdown(null);
                return;
            }
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            setCountdown({ days, hours, minutes, seconds, total: diff });
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [examDate]);

    if (props.userDetails[fields.year] !== 'PGY 4') return null;
    if (!examDate || !countdown) return null;

    const isToday = countdown.days === 0 && countdown.hours === 0 && countdown.minutes === 0 && countdown.seconds === 0;

    const formattedDate = new Date(examDate + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric'
    });

    const units = [
        { value: countdown.days, label: 'Days' },
        { value: countdown.hours, label: 'Hours' },
        { value: countdown.minutes, label: 'Min' },
        { value: countdown.seconds, label: 'Sec' },
    ];

    return (
        <Row className="mb-3">
            <Col xs="12">
                <div className="shadow" style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    borderRadius: '10px',
                    padding: '16px 20px',
                    borderLeft: '4px solid #2dce89',
                }}>
                    {isToday ? (
                        <div className="d-flex align-items-center justify-content-center py-2">
                            <i className="fas fa-star mr-2" style={{ color: '#ffd600', fontSize: '1.2rem' }}></i>
                            <strong style={{ color: '#fff', fontSize: '1.1rem' }}>
                                Boards Exam is today! You got this — go crush it!
                            </strong>
                            <i className="fas fa-star ml-2" style={{ color: '#ffd600', fontSize: '1.2rem' }}></i>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center justify-content-between flex-column flex-md-row">
                            {/* Left: icon + title + date */}
                            <div className="d-flex align-items-center mb-2 mb-md-0">
                                <div className="d-flex align-items-center justify-content-center mr-3" style={{
                                    width: '40px', height: '40px', borderRadius: '50%',
                                    backgroundColor: 'rgba(45,206,137,0.2)',
                                    flexShrink: 0,
                                }}>
                                    <i className="fas fa-calendar-check" style={{ color: '#2dce89', fontSize: '1.1rem' }}></i>
                                </div>
                                <div>
                                    <strong style={{ color: '#fff', fontSize: '0.95rem' }}>{getSpecialty(props.activeSpecialty).name} Boards Exam Countdown</strong>
                                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>{formattedDate}</div>
                                </div>
                            </div>

                            {/* Center: countdown boxes */}
                            <div className="d-flex align-items-center mb-2 mb-md-0">
                                {units.map((unit, idx) => (
                                    <div key={unit.label} className="d-flex align-items-center">
                                        <div style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                                            minWidth: '46px', padding: '6px 8px',
                                            backgroundColor: 'rgba(255,255,255,0.08)',
                                            border: '1px solid rgba(255,255,255,0.15)',
                                            borderRadius: '8px',
                                        }}>
                                            <span style={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2, color: '#fff' }}>
                                                {String(unit.value).padStart(2, '0')}
                                            </span>
                                            <span style={{ fontSize: '0.6rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.5px' }}>
                                                {unit.label}
                                            </span>
                                        </div>
                                        {idx < units.length - 1 && (
                                            <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.3)', margin: '0 5px', fontSize: '1.1rem' }}>:</span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Right: motivational text */}
                            <div className="text-center text-md-right" style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem', maxWidth: '200px' }}>
                                Keep up the momentum, every session counts!
                            </div>
                        </div>
                    )}
                </div>
            </Col>
        </Row>
    );
};

const mapStateToProps = state => {
    return {
        userDetails: state.loginAuth.userDetails,
        activeSpecialty: state.specialty.activeSpecialty,
    };
};

export default connect(mapStateToProps)(BoardsCountdownBanner);
